import { apiRequest } from './apiRequest'

const AFFILIATE_ME_CACHE_MS = 60 * 1000 // 60 วินาที — ลดการโหลดซ้ำเวลาเปิดหน้าแนะนำเพื่อน
let affiliateMeCache: { data: AffiliateMeData; at: number } | null = null
let affiliateMeInFlight: Promise<AffiliateMeData> | null = null

export interface AffiliateMeData {
  affiliateCode: string
  affiliateLink: string
  commissionRate: string
  referredMembers: number
  directReferredMembers?: number
  totalEarnings: number
  remainingEarnings: number
  calculationBasis?: 'loss' | 'mixed'
  payoutMode?: 'manual' | 'auto'
  userPayoutMode?: 'inherit' | 'manual' | 'auto'
  minCommissionWithdrawalAmount?: number
  totalsBySource?: {
    lottery?: number
    amb?: number
  }
}

export interface AffiliateMemberItem {
  username: string
  displayName: string
  joinedAt: string
  status: string
}

export interface AffiliateCommissionHistoryItem {
  _id?: string
  date?: string
  betAmount?: number
  commission?: number
  createdAt?: string
  sourceType?: 'lottery' | 'amb'
  referredUsername?: string
  referredDisplayName?: string
  level?: number
  category?: string
  providerId?: string
  providerName?: string
}

export interface AffiliateMeResponse {
  success: boolean
  data: AffiliateMeData
  message?: string
}

export interface AffiliateMembersResponse {
  success: boolean
  data: AffiliateMemberItem[]
  message?: string
}

export interface AffiliateCommissionHistoryResponse {
  success: boolean
  data: AffiliateCommissionHistoryItem[]
  message?: string
}

/**
 * Get current user's affiliate info (link, code, referred count, earnings).
 * ใช้ cache 60 วินาที เพื่อลดการโหลดซ้ำ
 */
export async function getAffiliateMe(forceRefresh = false): Promise<AffiliateMeData> {
  if (!forceRefresh && affiliateMeCache && Date.now() - affiliateMeCache.at < AFFILIATE_ME_CACHE_MS) {
    return affiliateMeCache.data
  }
  if (!forceRefresh && affiliateMeInFlight) return affiliateMeInFlight

  const promise = (async () => {
    try {
      const res = await apiRequest<AffiliateMeResponse>('/api/v1/user-auth/affiliate/me')
      if (!res.success || !res.data) {
        throw new Error((res as any).message || 'Failed to load affiliate data')
      }
      affiliateMeCache = { data: res.data, at: Date.now() }
      return res.data
    } finally {
      affiliateMeInFlight = null
    }
  })()

  affiliateMeInFlight = promise
  return promise
}

/**
 * ล้าง cache (เช่น หลังผู้ใช้กด refresh)
 */
export function clearAffiliateMeCache(): void {
  affiliateMeCache = null
  affiliateMeInFlight = null
}

/**
 * แปลง ref token จากลิงก์ ?ref=... เป็น referralCode (สำหรับหน้า landing/สมัคร)
 * ใช้เมื่อเปิดลิงก์แบบเข้ารหัส เพื่อกันการแก้ไขลิงก์เอง
 */
export async function resolveReferralToken(refToken: string): Promise<{ referralCode: string; tenantId?: string }> {
  const res = await apiRequest<{ success: boolean; data: { referralCode: string; tenantId?: string }; message?: string }>(
    `/api/v1/user-auth/affiliate/ref/resolve?ref=${encodeURIComponent(refToken)}`
  )
  if (!res.success || !res.data?.referralCode) {
    throw new Error((res as any).message || 'Invalid or expired referral link')
  }
  return { referralCode: res.data.referralCode, tenantId: res.data.tenantId }
}

/**
 * Get list of members referred by current user.
 */
export async function getAffiliateMembers(): Promise<AffiliateMemberItem[]> {
  const res = await apiRequest<AffiliateMembersResponse>('/api/v1/user-auth/affiliate/members')
  if (!res.success) {
    throw new Error((res as any).message || 'Failed to load referred members')
  }
  return Array.isArray(res.data) ? res.data : []
}

/**
 * Get commission history (date, bet amount, commission). Backend may return [] until commission is implemented.
 */
export async function getAffiliateCommissionHistory(limit?: number): Promise<AffiliateCommissionHistoryItem[]> {
  const qs = limit != null ? `?limit=${Math.min(limit, 100)}` : ''
  const res = await apiRequest<AffiliateCommissionHistoryResponse>(
    `/api/v1/user-auth/affiliate/commission-history${qs}`
  )
  if (!res.success) {
    throw new Error((res as any).message || 'Failed to load commission history')
  }
  return Array.isArray(res.data) ? res.data : []
}
